// Chunk load failure guard for every Vite page.
//
// A hashed Vite chunk can disappear under an open page: a deploy ships new
// hashes, or a local rebuild replaces public/vite-dev/assets while the server
// still renders the old manifest. Every lazy import then 404s and the page
// freezes (a black board with no error). This guard turns that into one
// automatic reload, and if the reload fails again within RETRY_WINDOW_MS, a
// plain "new version" message with a Reload button instead of a frozen page.
//
// It is a CLASSIC script on purpose, with no import or export, so it runs from
// two places: inlined into the layout head before the entry tag
// (ViteChunkGuardHelper#vite_chunk_guard_tag), which also catches a 404 on the
// entry script itself, and imported first by each entrypoint as a side effect,
// which covers pages without the tag. It is idempotent (window.__pgChunkGuard).
// It must never depend on another chunk: the chunk may be what is missing.
//
// The message text lives here, not in the i18n JSON, for the same reason: a
// locale chunk can be one of the missing files.
//
// See docs/reference/vite-frontend.md "Chunk load failures".
(function (w) {
  'use strict'
  if (!w || !w.document || w.__pgChunkGuard) return

  var STORAGE_KEY = 'pg:chunk-reload-at'
  var RETRY_WINDOW_MS = 5 * 60 * 1000
  var ENDPOINT = '/api/v2/live_load_metrics'
  var FAIL_REASON = 'chunk_failed'
  var OVERLAY_ID = 'pg-chunk-failed'
  // Hashed Vite output: /vite/assets/, /vite-dev/assets/, /vite-test/assets/.
  var CHUNK_URL = /\/vite[a-z-]*\/assets\//
  // Chrome, Firefox, Safari and Vite's own preload helper wording.
  var CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to load module script/i

  var MESSAGES = {
    en: ['A new version of Playgroup is available. Reload to continue.', 'Reload'],
    de: ['Eine neue Version von Playgroup ist verfügbar. Lade die Seite neu, um weiterzumachen.', 'Neu laden'],
    fr: ['Une nouvelle version de Playgroup est disponible. Rechargez la page pour continuer.', 'Recharger'],
    it: ['È disponibile una nuova versione di Playgroup. Ricarica la pagina per continuare.', 'Ricarica'],
    es: ['Hay una nueva versión de Playgroup disponible. Recarga la página para continuar.', 'Recargar'],
    ja: ['Playgroupの新しいバージョンがあります。続けるには再読み込みしてください。', '再読み込み'],
  }

  var doc = w.document
  var handled = false

  function errorText(reason) {
    if (!reason) return ''
    if (typeof reason === 'string') return reason
    return String(reason.message || reason)
  }

  function isChunkError(reason) {
    return CHUNK_ERROR.test(errorText(reason))
  }

  function isChunkUrl(url) {
    return typeof url === 'string' && CHUNK_URL.test(url)
  }

  // The Vite dev server reports its own errors (and a transform error would
  // otherwise reload the page under the developer). Only built bundles count.
  function usesDevServer() {
    try {
      return !!doc.querySelector('script[src*="/@vite/client"]')
    } catch (_) {
      return false
    }
  }

  // undefined = storage unavailable, null = no earlier reload.
  function readLastReload() {
    try {
      var raw = w.sessionStorage.getItem(STORAGE_KEY)
      return raw ? Number(raw) : null
    } catch (_) {
      return undefined
    }
  }

  function writeLastReload(at) {
    try {
      w.sessionStorage.setItem(STORAGE_KEY, String(at))
      return true
    } catch (_) {
      return false
    }
  }

  function isEmbedded() {
    try {
      return w.top !== w
    } catch (_) {
      return true
    }
  }

  // One LiveLoadMetric row (fail_reason chunk_failed) on board pages, the only
  // pages that know their game. Fire and forget, never throws.
  function beacon() {
    try {
      if (isEmbedded()) return
      var meta = doc.querySelector('meta[name="game-id"]')
      var gameId = meta && meta.getAttribute('content')
      if (!gameId) return
      var body = JSON.stringify({
        live_load_metric: {
          load_id: 'chunk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10),
          completed: false,
          fail_reason: FAIL_REASON,
          game_id: gameId,
          user_agent: (w.navigator && w.navigator.userAgent) || null,
        },
      })
      var nav = w.navigator
      if (nav && nav.sendBeacon && nav.sendBeacon(ENDPOINT, new w.Blob([body], { type: 'application/json' }))) return
      if (w.fetch) {
        w.fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
          credentials: 'same-origin',
        }).catch(function () {})
      }
    } catch (_) {}
  }

  function reloadPage() {
    w.location.reload()
  }

  function messageFor() {
    var lang = String(doc.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase()
    return MESSAGES[lang] || MESSAGES.en
  }

  function showMessage() {
    if (!doc.body) {
      doc.addEventListener('DOMContentLoaded', showMessage)
      return
    }
    if (doc.getElementById(OVERLAY_ID)) return
    var text = messageFor()
    var overlay = doc.createElement('div')
    overlay.id = OVERLAY_ID
    overlay.setAttribute('role', 'alertdialog')
    overlay.setAttribute('aria-live', 'assertive')
    // Inline styles only (no stylesheet may have loaded), with opacity and
    // animation set explicitly: the board layout hides body children until boot.
    overlay.style.cssText =
      'position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483647;opacity:1;animation:none;' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;' +
      'background:#0e1513;color:#DDE4E1;text-align:center;' +
      "font:500 18px/1.4 Roboto,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;"
    var p = doc.createElement('p')
    p.style.cssText = 'margin:0 0 20px;max-width:28em;'
    p.textContent = text[0]
    var button = doc.createElement('button')
    button.type = 'button'
    button.textContent = text[1]
    button.style.cssText =
      'font:inherit;font-weight:600;padding:10px 28px;border:0;border-radius:999px;' +
      'background:#1CDECA;color:#0e1513;cursor:pointer;'
    button.addEventListener('click', function () {
      api.reload()
    })
    overlay.appendChild(p)
    overlay.appendChild(button)
    doc.body.appendChild(overlay)
  }

  // Reload once; a second failure inside the window (or no storage to
  // remember the first one) shows the message instead, so it can never loop.
  function recover() {
    var now = Date.now()
    var last = readLastReload()
    if (last === undefined || (last && now - last < RETRY_WINDOW_MS)) {
      showMessage()
      return
    }
    if (!writeLastReload(now)) {
      showMessage()
      return
    }
    api.reload()
  }

  function handle() {
    if (handled || usesDevServer()) return
    handled = true
    beacon()
    // Offline is not a new version: reloading now would land on the browser's
    // offline page. Recover once the connection is back.
    if (w.navigator && w.navigator.onLine === false) {
      w.addEventListener('online', recover, { once: true })
      return
    }
    recover()
  }

  // Vite's preload helper dispatches this for a failed lazy import or CSS
  // preload. Not prevented: the original error still reaches the caller's own
  // catch (a locale load falls back to English) until the reload happens.
  w.addEventListener('vite:preloadError', function (event) {
    handle(event && event.payload)
  })

  // A failed import() that bypasses the preload helper.
  w.addEventListener('unhandledrejection', function (event) {
    if (event && isChunkError(event.reason)) handle(event.reason)
  })

  // A 404 on a <script> or <link> for a hashed asset, the entry script
  // included. Load errors do not bubble, so this listens in the capture phase.
  w.addEventListener(
    'error',
    function (event) {
      var el = event && event.target
      if (!el || el === w || !el.tagName) return
      var tag = String(el.tagName).toUpperCase()
      if (tag !== 'SCRIPT' && tag !== 'LINK') return
      if (isChunkUrl(el.src || el.href)) handle(el.src || el.href)
    },
    true
  )

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    RETRY_WINDOW_MS: RETRY_WINDOW_MS,
    OVERLAY_ID: OVERLAY_ID,
    isChunkError: isChunkError,
    isChunkUrl: isChunkUrl,
    handle: handle,
    showMessage: showMessage,
    reload: reloadPage,
  }
  w.__pgChunkGuard = api
})(typeof window !== 'undefined' ? window : undefined)
